import { useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { FormikContext } from 'formik';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '../Typography';
import CheckboxField from './CheckboxField';
import { FormTrackingContext } from '../Form';
import { logFieldChange } from '../../utils/tracking';
import InfoButton from '../InfoButton';
import { CommonProps } from './types';
import { COLORS, ROUNDED_PANEL_SX } from '../../themev2';
import { ChevronDownIcon } from '../../components/Icons';

const SelectChevronIcon = ({ className }: { className?: string }) => (
  <ChevronDownIcon width={20} height={20} color={COLORS.gray[1100]} className={className} />
);

export type MultiSelectOption = { value: string; label: string; [key: string]: any };

type MultiSelectFieldProps<T extends MultiSelectOption = MultiSelectOption> = CommonProps & {
  value?: string[];
  options: T[];
  startAdornment?: React.ReactNode;
  maxSelections?: number;
  showChevron?: boolean;
  roundedPanel?: boolean;
  renderOption?: (
    option: T,
    checked: boolean,
    onChange: (checked: boolean) => void,
    disabled: boolean
  ) => ReactNode;
};

export default function MultiSelectField<T extends MultiSelectOption = MultiSelectOption>({
  fullWidth = true,
  error,
  name,
  label,
  onChange,
  value,
  options,
  placeholder,
  helperText,
  eventLogger = logFieldChange,
  moreInfoTooltip = null,
  disabled = false,
  startAdornment,
  maxSelections,
  showChevron = false,
  roundedPanel = false,
  renderOption,
  sx = {},
}: MultiSelectFieldProps<T>) {
  const formik = useContext(FormikContext);
  const _error = error || (formik?.touched[name] && formik?.errors[name]);
  const controlRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>();
  const [selected, setSelected] = useState<string[]>(
    (value?.length ? value : (formik?.values[name] as string[])) ?? []
  );
  // A parent that drives `value` — no Formik, or a value that changes for
  // reasons other than this field, like prefilling from a client's stored
  // preference — must win over the internal checkbox state. Comparing the
  // serialized array keeps this field's own toggles from bouncing back through
  // here, and consumers that never echo `value` back are left alone.
  const serializedValue = value ? JSON.stringify(value) : undefined;
  const appliedValueRef = useRef(serializedValue);
  useEffect(() => {
    if (serializedValue === undefined || serializedValue === appliedValueRef.current) return;
    appliedValueRef.current = serializedValue;
    setSelected(JSON.parse(serializedValue));
  }, [serializedValue]);
  const { trackingEvent } = useContext(FormTrackingContext);

  return (
    <FormControl ref={controlRef} fullWidth={fullWidth} error={!!_error}>
      <FormLabel sx={{ padding: '8px 0', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Typography>{label}</Typography>
          {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
        </div>
      </FormLabel>
      <Select
        startAdornment={
          startAdornment ? (
            <InputAdornment
              position="start"
              style={{
                width: 'auto',
                color: '#3B4567',
              }}
            >
              {startAdornment}
            </InputAdornment>
          ) : null
        }
        color="secondary"
        disabled={disabled}
        IconComponent={showChevron ? SelectChevronIcon : undefined}
        onOpen={() => setMenuWidth(controlRef.current?.clientWidth)}
        MenuProps={{
          PaperProps: {
            style: menuWidth ? { width: menuWidth } : undefined,
            sx: roundedPanel ? ROUNDED_PANEL_SX : undefined,
          },
        }}
        multiple
        value={selected}
        onChange={(e) => {
          const value = e.target.value;
          formik && formik.setFieldValue(name, value);
          onChange && onChange(value);
        }}
        onBlur={(e) => {
          const value = e.target.value;
          trackingEvent && eventLogger(trackingEvent, name, value);
        }}
        variant="outlined"
        displayEmpty
        renderValue={(value) =>
          !!value.length ? (
            value.map((v) => options.find((option) => option.value === v)?.label).join(', ')
          ) : (
            <Typography style={{ color: COLORS.gray[1000] }}>{placeholder}</Typography>
          )
        }
        sx={{
          background: disabled ? COLORS.gray[300] : COLORS.brand.white,
          '.MuiSelect-select': {
            padding: '12px',
          },
          '& fieldset': {
            borderColor: COLORS.gray[900],
            borderRadius: '8px',
          },
          ...sx,
        }}
      >
        {options.map((option) => {
          const isDisabled = maxSelections
            ? selected.length >= maxSelections && !selected.includes(option.value)
            : false;
          const isChecked = selected.includes(option.value);
          const handleChange = (checked: boolean) => {
            if (checked) {
              if (maxSelections && selected.length >= maxSelections) {
                return;
              }
              setSelected((prev) => [...prev, option.value]);
            } else {
              setSelected((prev) => prev.filter((elem) => elem !== option.value));
            }
          };

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={isDisabled}
              sx={{
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: COLORS.gray[200],
                },
              }}
            >
              {renderOption ? (
                renderOption(option, isChecked, handleChange, isDisabled)
              ) : (
                <CheckboxField
                  checked={isChecked}
                  label={option.label}
                  disabled={isDisabled}
                  onChange={handleChange}
                />
              )}
            </MenuItem>
          );
        })}
      </Select>
      {(!!_error || helperText) && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
